import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6"

import { UserManagementCentreWrapper } from "./styles"
import Header from "./components/header"
import Tabs from "./components/tabs"
import FormInputsNewMember from "./components/formInputsNewMember"
import FormInputsServiceProvider from "./components/formInputsServiceProvider"
import FormInputsAddOwner from "./components/formInputsAddOwner"
import { useContext, useState, useEffect } from "react"
import {
	APIS_KEYS,
	usePostCreateServiceProvider,
	usePostCreateTeamMember,
	useOwnerManagment,
} from "@services"
import { useQueryClient } from "@tanstack/react-query"
import { ToastMessageContext } from "@context/toast-message-cotnext"
import { LoadingWrapper } from "@components"

interface IUserManagementCentreProps {
	onclose: () => void
}
interface IFormUserManagementCentreNewMember {
	userType: number
	fullName: string
	email: string
	phoneNumber: string
	roleId: number
}

interface IFormUserManagementCentreServiceProvider {
	fullName: string
	phoneNumber: string
	selectedServices: []
}

interface IFormUserManagementCentreAddOwner {
	ownerId: number
	email: string
}

export type IFormUserManagementCentre =
	| IFormUserManagementCentreNewMember
	| IFormUserManagementCentreServiceProvider
	| IFormUserManagementCentreAddOwner

export default function UserManagementCentre({
	onclose,
}: IUserManagementCentreProps) {
	/* ------------------------------- Local State ------------------------------ */
	const [activeTab, setActiveTab] = useState<1 | 2 | 3>(1)
	/* ---------------------------------- API'S --------------------------------- */
	const { mutateAsync, isPending } = usePostCreateTeamMember()
	const {
		mutateAsync: mutateAsyncPostCreateServiceProvider,
		isPending: isPendingPostCreateServiceProvider,
	} = usePostCreateServiceProvider()
	const { AddOwnerUserMutate } = useOwnerManagment()
	const { mutateAsync: mutateAsyncAddOwnerUser, isPending: isPendingAddOwnerUser } = AddOwnerUserMutate({
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: [APIS_KEYS.GETALLOWNERUSERS],
			})
			showSuccess(t("UserManagement.owner_access_granted_successfully"))
			onclose()
		},
		onError: (error) => {
			console.error('Error adding owner user:', error)
		}
	})

	/* ---------------------------------- Hooks --------------------------------- */
	const { showSuccess } = useContext(ToastMessageContext)
	const queryClient = useQueryClient()
	const {
		t,
		i18n: { language },
	} = useTranslation()

	const methods = useForm<IFormUserManagementCentre>({
		mode: "onChange", // Enable real-time validation
		reValidateMode: "onChange",
		defaultValues: {
			fullName: '',
			email: '',
			phoneNumber: '',
			roleId: undefined,
			ownerId: undefined,
			selectedServices: []
		}
	})

	// Reset form when switching tabs to clear previous values
	useEffect(() => {
		methods.reset({
			fullName: '',
			email: '',
			phoneNumber: '',
			roleId: undefined,
			ownerId: undefined,
			selectedServices: []
		})
	}, [activeTab, methods])

	const onSubmit = async (data: IFormUserManagementCentre) => {
		if (activeTab === 1) {
			const response = await mutateAsync(data)
			if (response.status === 200) {
				queryClient.invalidateQueries({
					queryKey: [APIS_KEYS.GETALLOWNERUSERS],
				})
				showSuccess(
					t("UserManagement.a_member_has_been_added_to_the_team_successfully")
				)
				onclose()
			}
		}
		if (activeTab === 2) {
			const response = await mutateAsyncPostCreateServiceProvider(data)
			if (response.status === 200) {
				queryClient.invalidateQueries({
					queryKey: [APIS_KEYS.GETALLOWNERUSERS],
				})
				showSuccess(t("UserManagement.service_provider_added_successfully"))
				onclose()
			}
		}
		if (activeTab === 3) {
			const ownerData = data as IFormUserManagementCentreAddOwner
			await mutateAsyncAddOwnerUser({
				ownerId: ownerData.ownerId.toString(),
				email: ownerData.email
			})
		}
	}

	return (
		<UserManagementCentreWrapper $lang={language as "ar" | "en"}>
			<LoadingWrapper loading={isPending || isPendingPostCreateServiceProvider || isPendingAddOwnerUser}>
				<div className='backAction'>
					<div onClick={onclose}>
						{language === "ar" ? <FaArrowLeftLong /> : <FaArrowRightLong />}
					</div>
				</div>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<div className='containerFormElements'>
							<Header />
							<Tabs setActiveTab={setActiveTab} activeTab={activeTab} />
							{activeTab === 1 && <FormInputsNewMember />}
							{activeTab === 2 && <FormInputsServiceProvider />}
							{activeTab === 3 && <FormInputsAddOwner />}
						</div>
					</form>
				</FormProvider>
			</LoadingWrapper>
		</UserManagementCentreWrapper>
	)
}
